import { useState } from "react";
import { Lock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { Switch } from "@/components/ui/switch";

const ITEMS = [
  ["genomic_processing", "Genomic data processing", "Allow CareStop to store and evaluate genetic markers.", "genomic-data-consent-toggle"],
  ["health_data_sharing", "Health check data sharing", "Share routine health metrics with the treating care team.", "health-sharing-toggle"],
  ["ai_drafting", "AI-assisted drafting", "Allow an AI model to draft the summary that your doctor reviews. If off, only rule-based templates are used.", "ai-drafting-toggle"],
  ["lab_access", "Laboratory access", "Let diagnostic labs upload results to this record.", "lab-access-toggle"],
  ["nutritionist_access", "Nutritionist access", "Share released diet & lifestyle guidance with a nutritionist.", "nutritionist-access-toggle"],
  ["insurance_access", "Insurance access (scoped)", "Insurance can see released summaries only — never genomic detail or AI drafts.", "insurance-sharing-toggle"],
];

export function ConsentPanel({ patient: p, onChanged }) {
  const [consent, setConsent] = useState(p.consent || {});
  const [busy, setBusy] = useState(false);
  const update = async (changes) => {
    setBusy(true);
    try { const { data } = await api.put(`/patients/${p.id}/consent`, changes); setConsent(data.consent); toast.success("Consent updated and logged"); onChanged(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const revokeAll = () => update({ nutritionist_access: false, insurance_access: false, lab_access: false });
  const active = Object.values(consent).filter(Boolean).length;

  return (
    <div className="space-y-4" data-testid="consent-management-panel">
      <div className="clinical-card p-5 sm:p-6 flex flex-wrap items-center gap-4">
        <span className="w-11 h-11 rounded-xl bg-green-50 text-green-700 grid place-items-center"><ShieldCheck className="w-5 h-5" /></span>
        <div className="flex-1"><h2 className="font-heading font-semibold text-slate-900">DPDP consent matrix</h2><p className="text-sm text-slate-600">{active} of {ITEMS.length} permissions active · last updated {fmtDateTime(p.consent_updated_at)}</p></div>
        <button data-testid="revoke-consent-button" disabled={busy} onClick={revokeAll} className="text-sm font-semibold text-rose-700 border border-rose-200 bg-rose-50 rounded-full px-4 py-2 hover:bg-rose-100">Revoke all third-party sharing</button>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {ITEMS.map(([k, label, desc, tid]) => (
          <div key={k} className={`clinical-card p-4 flex items-start gap-4 ${consent[k] ? "" : "bg-slate-50/60"}`} data-testid={`consent-row-${k}`}>
            <span className={`mt-0.5 w-8 h-8 rounded-lg grid place-items-center ${consent[k] ? "bg-green-50 text-green-700" : "bg-rose-50 text-rose-700"}`}>{consent[k] ? <ShieldCheck className="w-4 h-4" /> : <Lock className="w-4 h-4" />}</span>
            <div className="flex-1"><p className="font-medium text-slate-900 text-sm">{label}</p><p className="text-xs text-slate-500 mt-0.5">{desc}</p></div>
            <Switch data-testid={tid} checked={!!consent[k]} disabled={busy} onCheckedChange={(v) => update({ [k]: v })} className="data-[state=checked]:bg-teal-700" />
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-500">Every consent change is recorded in the immutable audit trail with actor, role and timestamp.</p>
    </div>
  );
}
